<template>
  <div
    :id="'remark-' + id"
    v-click-outside="{ handler: handleClickOutside, detectIframe: false }"
    :class="[
      {
        'sf-hover': !sticky,
        'sf-commcard-user-can-check': todo && allowedActions.toggleDone,
        'sf-commcard-completed': done,
        'sf-commcard--inlined': inlinedVariant,
      },
      highlightClass,
    ]"
    :data-liquid-input-field-path="liquidInputFieldPath"
    :data-testid="'remark-' + id"
    :data-text-property-value="textPropertyValue"
    :type="remarkType"
    class="sf-commcard sf-below-3"
    @click.stop="handleContainerClick"
  >
    <div class="sf-commcard-message">
      <div
        class="sf-commcard-message-content"
        :class="{ 'sf-commcard-message-content--inlined': inlinedVariant }"
      >
        <div
          v-if="todo"
          class="
            sf-commcard-message-content-toggle sf-form-check sf-form-check-todo
          "
        >
          <div class="sf-form-check-content">
            <div
              class="sf-form-check-indicator"
              @click.prevent="
                allowedActions.toggleDone ? toggleTodoDone(id) : null
              "
            ></div>
          </div>
        </div>
        <div
          class="
            sf-commcard-message-content-post sf-commcard-post sf-full-width
          "
        >
          <div
            v-if="showPeriod"
            class="sf-commcard-post-period sf-label sf-label-rectangular"
          >
            {{ ledgerPeriodName }}
          </div>
          <div v-if="showContext" class="sf-commcard-post-context sf-uppercase">
            {{ contextCaption }}
          </div>
          <div v-if="showContextBtn && !isCompanyContext" class="sf-pad-b-2">
            <a :href="contextUrl" class="sf-btn sf-btn-small">{{
              contextBtn
            }}</a>
          </div>
          <div class="sf-commcard-post-author">
            <div class="sf-commcard-post-name">
              <i v-if="createdByBot" class="i-bot"></i> {{ authorName }}
            </div>
            <div
              v-if="editedAt"
              v-tooltip.timestamp="editedAt"
              class="sf-commcard-post-date"
            >
              {{ I18n.t('communication.cards.edited') }}
              {{ editedAtFormatted }}
            </div>
            <div
              v-else
              v-tooltip.timestamp="createdAt"
              class="sf-commcard-post-date"
            >
              {{ createdAtFormatted }}
            </div>
            <i
              v-if="hashCheckDiff"
              v-tooltip="I18n.t('communication.cards.hash_check_tooltip')"
              class="fa fa-info-circle"
            />
            <i
              v-if="hasDraft"
              v-tooltip="I18n.t('communication.cards.pending_draft_tooltip')"
              class="fa fa-edit"
            ></i>
            <RemarkBubble
              v-if="pdfAnnotation"
              :id="'remark-' + id"
              :emit-click="true"
              :linking-path="pdfAnnotation.xfdfId"
              :prevent-bubble-unlink="preventBubbleUnlink"
              variant="default"
              @click.stop="gotoPdfAnnotation"
            />
            <RemarkBubble
              :id="'remark-' + id"
              :linking-path="liquidInputFieldPath"
              :prevent-bubble-unlink="preventBubbleUnlink"
            />
          </div>
          <editable-remark-tags-list
            :allowed-actions="allowedActions"
            :remark-id="id"
            :tag-ids="tagIds"
            @tags-updated="tagsUpdated"
          ></editable-remark-tags-list>
          <div
            v-dompurify-html="renderedText"
            class="sf-commcard-post-text"
          ></div>
          <div
            v-if="doneAt && done && doneByName"
            v-tooltip.timestamp="doneAt"
            class="sf-tooltip-el-wrapper"
          >
            <div class="sf-level sf-font-xs sf-c-text-light sf-below-1">
              {{
                I18n.t('communication.cards.user_marked_as_done', {
                  user: doneByName,
                })
              }}
            </div>
          </div>
          <div
            v-for="attachment in attachments"
            :key="attachment.id"
            class="sf-label sf-below-1 sf-level-item"
          >
            <div class="sf-level sf-level-small">
              <a
                :href="attachment.url"
                class="sf-a sf-level-item"
                target="_blank"
                >{{ attachment.filename }}</a
              >
            </div>
          </div>
          <div class="sf-commcard-post-actions">
            <a
              v-if="showCommentsToggle"
              href="#"
              @click.prevent="toggleShowAllComments"
              >{{
                I18n.t(
                  'communication.comments.show_all_toggle.' +
                    commentsVisibilityState,
                  { count: allCommentsCount }
                )
              }}</a
            ><a
              v-if="allowedActions.edit"
              href="#"
              @click.prevent="openEditRemarkModal"
              >{{ I18n.t('common.edit') }}</a
            ><a
              v-if="allowedActions.remove"
              href="#"
              @click.prevent="handleRemoveClick"
              >{{ I18n.t('common.remove') }}</a
            >
          </div>
        </div>
      </div>
      <div
        :class="{ 'sf-hover-item': !sticky }"
        class="sf-commcard-message-side"
      >
        <div
          v-if="(allowedActions.pin || sticky) && !recurringTodoTemplateId"
          :class="{
            'sf-commcard-side-pinned-active': sticky,
            'sf-not-allowed': isPinAllowed,
          }"
          class="sf-commcard-side-pinned"
          @click="handlePinClick"
        >
          <span class="fa fa-thumb-tack"></span>
        </div>
        <div
          v-if="allowedActions.follow || followedByCurrentUser"
          :class="{
            'sf-commcard-side-notifications-active': followedByCurrentUser,
            'sf-not-allowed': !allowedActions.follow,
          }"
          class="sf-commcard-side-notifications"
          @click="handleFollowClick"
        >
          <span class="fa fa-bell"></span>
        </div>
      </div>
    </div>
    <div class="sf-commcard-comments">
      <comment-card
        v-for="comment in comments"
        :key="comment.id"
        :commentable-id="id"
        commentable-type="Remark"
        v-bind="comment"
      ></comment-card>
    </div>
    <div
      v-if="!showNewCommentForm"
      class="sf-commcard-reply-placeholder"
      :class="{ 'sf-commcard-reply-placeholder--inlined': inlinedVariant }"
      @click="addNewCommentRemarkId(id)"
    >
      {{ I18n.t('communication.comments.enter_reply') }}
    </div>
    <comment-form
      v-else
      :commentable-id="id"
      :follower-ids="followerIds"
      btn-text="Reply"
      commentable-type="Remark"
      text=""
    ></comment-form>
  </div>
</template>

<script>
import api from '../../api/remark'

import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import snakeCase from 'lodash/snakeCase'
import { mapActions, mapGetters, mapState } from 'vuex'

import commentsVisibility from '../mixins/comments-visibility'
import CommentCard from '../comments/comment-card'
import CommentForm from '../comments/comment-form'
import EditableRemarkTagsList from './editable-remark-tags-list'
import RemarkBubble from '@app/application/communication/sections/communication/components/remarks/remark-bubble'

import I18n from 'i18n-js'
import remarkDraftLoadMixin from '../../../../../../shared/vue/components/new-remark/mixins/remark-draft-load-mixin'
import { SfLog } from '@app/adapters/logger'
import { REMARK_ACTIONS } from '@app/application/communication/constants'
import communicationDate from '@app/utils/communication-date'
import truncate from '@app/utils/truncate'
import { variants } from './constants'

import { useBubbleStore } from '@app/application/communication/store/bubbles'
import { useHighlightedCard } from '@app/shared/pinia/highlighted-card'

export default {
  components: {
    CommentCard,
    CommentForm,
    EditableRemarkTagsList,
    RemarkBubble,
  },
  mixins: [commentsVisibility, remarkDraftLoadMixin],
  // these props are passed to this component via v-bind in e.g. sidepane-communication and other places
  props: {
    id: {
      type: Number,
      required: true,
    },
    showContext: {
      type: Boolean,
    },
    showContextBtn: {
      type: Boolean,
      default: false,
    },
    contextId: {
      type: Number,
      required: true,
    },
    contextType: {
      type: String,
      required: true,
    },
    ledgerPeriodName: {
      type: String,
      required: true,
    },
    ledgerId: {
      type: Number,
      required: true,
    },
    endLedgerPeriodName: {
      type: String,
      required: false,
    },
    endLedgerId: {
      type: Number,
      required: false,
    },
    ledgerArchived: {
      type: Boolean,
      required: true,
    },
    authorId: {
      type: Number,
      required: true,
    },
    companyId: {
      type: Number,
      required: true,
    },
    preventBubbleUnlink: {
      type: Boolean,
      required: false,
      default: false,
    },
    recurringTodoTemplateId: {
      type: Number,
      required: false,
      default: null,
    },
    botName: {
      type: String,
      required: false,
    },
    createdAt: {
      type: String,
      required: true,
    },
    updatedAt: {
      type: String,
      required: true,
    },
    editedAt: {
      type: String,
    },
    text: {
      type: String,
      required: true,
    },
    renderedText: {
      type: String,
      required: true,
    },
    sticky: {
      type: Boolean,
      required: true,
    },
    followerIds: {
      type: Array,
      required: true,
    },
    todo: {
      type: Boolean,
      required: true,
    },
    done: {
      type: Boolean,
      required: true,
    },
    doneAt: {
      type: String,
      required: false,
    },
    doneByName: {
      type: String,
      required: false,
    },
    attachmentIds: {
      type: Array,
      required: true,
    },
    commentIds: {
      type: Array,
      required: true,
    },
    allowedActions: {
      type: Object,
      required: true,
    },
    showPeriod: {
      type: Boolean,
      default: false,
    },
    openReply: {
      type: Boolean,
      default: false,
    },
    liquidInputFieldPath: {
      type: String,
    },
    textPropertyValue: {
      type: String,
    },
    tagIds: {
      type: Array,
      required: true,
    },
    showTags: {
      type: Boolean,
      required: false,
      default: true,
    },
    requestedForOwner: {
      type: Boolean,
      required: false,
      default: true,
    },
    pdfAnnotation: {
      type: Object,
      required: false,
    },
    hashCheckDiff: {
      type: Boolean,
      required: false,
      default: false,
    },
    variant: {
      type: String,
      required: false,
      default: variants.default,
    },
    hideTypeSelector: {
      type: Boolean,
      required: false,
      default: false,
    },
    hidePeriodSelector: {
      type: Boolean,
      required: false,
      default: false,
    },
    editTitle: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    // this _needs_ to be a local data item (vs computed), so we can manually reload it from localstorage
    // when the edit remark modal is closed (user might've modified the text and now there's a draft)
    return {
      draft: null,
      remarkModalWasOpened: true, // default true to load the draft on mount
    }
  },
  computed: {
    ...mapState({
      newCommentRemarkIds: (state) => state.ui.newCommentRemarkIds,
      allRemarkTags: (state) =>
        state.remarkTags ? state.remarkTags.allRemarkTags : null,
      storeLedgerId: (state) => state.ledgerId,
    }),

    ...mapPiniaState(useHighlightedCard, ['highlightedCard']),

    ...mapGetters([
      'contextByLedgerTypeAndId',
      'userById',
      'attachmentArray',
      'commentsByIds',
    ]),

    createdByBot() {
      return !!this.botName
    },

    contextCaption() {
      return this.contextByLedgerTypeAndId(
        this.storeLedgerId || this.ledgerId,
        this.contextType,
        this.contextId
      ).caption
    },

    contextBtn() {
      return I18n.t(
        'communication.cards.caption_btn.' + snakeCase(this.contextType)
      )
    },

    contextUrl() {
      return (
        this.contextByLedgerTypeAndId(
          this.storeLedgerId || this.ledgerId,
          this.contextType,
          this.contextId
        ).url +
        '?current_ledger_id=' +
        this.ledgerId
      )
    },

    createdAtFormatted() {
      return communicationDate(this.createdAt)
    },

    editedAtFormatted() {
      return communicationDate(this.editedAt)
    },

    isCompanyContext() {
      return this.contextType === 'Company'
    },

    isDocumentContext() {
      return this.contextType === 'Document'
    },

    authorName() {
      if (this.createdByBot) {
        return this.botName
      } else {
        return this.userById(this.authorId).name
      }
    },

    followedByCurrentUser() {
      const currentUserId = parseInt(Silverfin.currentUserId())
      return this.followerIds.includes(currentUserId)
    },

    attachments() {
      const contextualAttachments = this.attachmentArray.filter(
        (attachment) => attachment && this.attachmentIds.includes(attachment.id)
      )

      return contextualAttachments.map((attachment) => ({
        ...attachment,
        filename: truncate(attachment.filename),
      }))
    },

    comments() {
      switch (this.commentsVisibilityState) {
        case 'default':
          return this.allComments.slice(-3)
        case 'all':
          return this.allComments
        case 'none':
          return []
        default:
          return []
      }
    },

    showNewCommentForm() {
      return this.openReply || this.newCommentRemarkIds.includes(this.id)
    },

    remarkType() {
      return this.todo ? 'todo' : 'note'
    },

    highlighted() {
      const { type, id } = this.highlightedCard
      return type === 'remark' && id === this.id
    },

    highlightClass() {
      if (this.highlightedCard.id === 0) return ''
      return this.highlighted ? 'sf-layer-shadow' : 'sf-dim-el'
    },
    hasDraft() {
      return !!this.draft && this.draft !== this.text
    },
    isOtherCardHighlighted() {
      const { id } = this.highlightedCard
      return id && id !== this.id
    },
    inlinedVariant() {
      return this.variant === variants.inlined
    },
    isPinAllowed() {
      return !this.allowedActions.pin || Silverfin.isCurrentUserPulse()
    },
  },
  created() {
    this.SHOW_COMMENTS_TOGGLE_TRESHOLD = 3
  },
  mounted() {
    this.loadDraftFromLocalStorage()
    window.eventHub.$on('modal-closed', this.loadDraftFromLocalStorage)
    window.eventHub.$on('openEditRemarkModal', this.openEditRemarkModalExternal)
    window.eventHub.$on(
      'clearHighlightedCard',
      this.clearHighlightedCardExternal
    )
    // focusCommentForm is received by both:
    // RemarkCard: if comment form is not rendered yet, we need to render it
    // CommentForm: if comment form is rendered, focus the correct comment form based on commentable type/id
    window.eventHub.$on('focusCommentForm', this.focusCommentForm)

    // if highlighted, and in the context of the pdf viewer, select the corresponding annotation on load
    if (this.highlighted && this.requestedForOwner && this.pdfAnnotation) {
      this.gotoPdfAnnotation()
    }
  },
  beforeUnmount() {
    window.eventHub.$off('modal-closed', this.loadDraftFromLocalStorage)
    window.eventHub.$off(
      'openEditRemarkModal',
      this.openEditRemarkModalExternal
    )
    window.eventHub.$off(
      'clearHighlightedCard',
      this.clearHighlightedCardExternal
    )
    window.eventHub.$off('focusCommentForm', this.focusCommentForm)
  },
  methods: {
    ...mapActions([
      'toggleTodoDone',
      'setRemarkSticky',
      'toggleRemarkFollow',
      'addNewCommentRemarkId',
    ]),
    ...mapPiniaActions(useBubbleStore, ['setHighlightedBubble']),
    ...mapPiniaActions(useHighlightedCard, ['unsetHighlightedCard']),

    clearHighlightedCardExternal() {
      if (this.highlighted) {
        this.clearHighlightedCard()
      }
    },

    clearHighlightedCard() {
      this.unsetHighlightedCard()
      this.setHighlightedBubble('')
      window.eventHub.$emit('clearAnchor')
    },

    handleClickOutside() {
      if (this.highlighted) {
        this.clearHighlightedCard()
      }
    },

    handleContainerClick() {
      if (this.isOtherCardHighlighted) {
        // Remark cards stop propagation of click event and handle unset on their own
        this.clearHighlightedCard()
      }
    },

    handlePinClick() {
      if (this.allowedActions.pin) {
        this.setRemarkSticky([this.id, !this.sticky])
      }
    },

    handleFollowClick() {
      if (this.allowedActions.follow) {
        this.toggleRemarkFollow(this.id)
      }
    },

    handleRemoveClick() {
      if (this.sticky) return this.handleRemovePinned()
      if (this.recurringTodoTemplateId !== null)
        return this.handleRemoveRecurringTodo()

      window.sfConfirm({
        onConfirm: this.destroyMessage,
      })
    },

    async destroyMessage(periodScope = null) {
      try {
        await api.destroy(this.id, this.companyId, periodScope)
        window.eventHub.$emit('refreshCommunicationData', {
          action: REMARK_ACTIONS.DESTROYED,
          remark: { id: this.id },
        })
      } catch (e) {
        SfLog.warn('Error while removing message', {
          extra: {
            error: e,
          },
        })
      }
    },

    handleRemovePinned() {
      window.modalCitizen.openModal('pinned-message-removal-modal', {
        onConfirm: async (allPeriods) => {
          try {
            if (allPeriods) {
              await this.destroyMessage()
              return
            }

            await api.destroyOnwards(this.id, this.companyId)
            window.eventHub.$emit('refreshCommunicationData', {
              action: REMARK_ACTIONS.DESTROYED,
              remark: { id: this.id },
            })
          } catch (e) {
            SfLog.warn('Error while partially removing message', {
              extra: {
                error: e,
              },
            })
          }
        },
      })
    },

    handleRemoveRecurringTodo() {
      window.modalCitizen.openModal('recurring-todo-removal-modal', {
        onConfirm: async (periodScope) => {
          this.destroyMessage(periodScope)
        },
      })
    },

    openEditRemarkModalExternal({ remarkId }) {
      if (this.id === remarkId) {
        this.openEditRemarkModal()
      }
    },

    openEditRemarkModal() {
      this.remarkModalWasOpened = true
      window.modalCitizen.openModal('edit-remark', { originalRemark: this })
    },

    tagsUpdated({ remark }) {
      window.eventHub.$emit('refreshCommunicationData', {
        remark,
        action: REMARK_ACTIONS.EDITED,
      })

      window.eventHub.$emit('scrollToElement', {
        elementType: 'remark',
        elementId: this.id,
      })
    },
    // modal-closed event is received by all remark-card components.
    // as such, reload the draft from localstorage only for the remark-card that
    // actually opened the modal, in case the user changed the text
    loadDraftFromLocalStorage() {
      if (this.remarkModalWasOpened) {
        this.remarkModalWasOpened = false
        this.draft = this.loadDraft()
      }
    },
    gotoPdfAnnotation() {
      const { xfdfId } = this.pdfAnnotation

      if (this.requestedForOwner) {
        window.eventHub.$emit('selectPdfAnnotation', xfdfId)
      } else {
        window.open(
          this.contextUrl +
            `&annotationId=${xfdfId}&current_ledger_id=${Silverfin.currentLedgerId()}`,
          '_blank'
        )
      }
    },
    focusCommentForm({ commentableId, commentableType }) {
      if (commentableId === this.id && commentableType === 'Remark') {
        this.addNewCommentRemarkId(this.id)
      }
    },
  },
}
</script>

<style lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.sf-commcard--inlined {
  @include background-color('neutral', 'subtle-1');

  margin: 0 -#{space(3)} !important; /* sf-below-3 uses important, so we need it here */
  padding: 0;
  border: 0;
  border-radius: 0;
  border-left: 4px solid -color('snow');
}

.sf-commcard-message-content--inlined {
  padding: space(2) space(3);
}

.sf-commcard-reply-placeholder--inlined {
  @include background-color('snow', 'lighter');
}
</style>
